import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "@/shared/i18n";
import { Button } from "@/shared/ui/button";
import { TableCell, TableRow } from "@/shared/ui/table";
import { formatCurrency, formatPercent } from "@/shared/utils/format";
import { formatDateShort } from "@/shared/utils/date";
import type { FinancialOverviewSummaryByCreator } from "../types";
import { CreatorReceivablesByMonth } from "./CreatorReceivablesByMonth";

interface Props {
  creator: FinancialOverviewSummaryByCreator;
  currency: string;
  isExpanded: boolean;
  onToggle: () => void;
}

export function CreatorRow({ creator, currency, isExpanded, onToggle }: Props) {
  const { t, i18n } = useTranslation("admin-financial-overview");
  const canExpand = creator.receivables.length > 0;

  return (
    <>
      <TableRow>
        <TableCell>
          {canExpand ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? t("collapseRow") : t("expandRow")}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          ) : null}
        </TableCell>
        <TableCell>
          <div className="flex flex-col">
            <span>
              <span className="font-medium">{creator.creatorName}</span>
              <span className="text-muted-foreground">
                {" "}
                - @{creator.creatorUsername}
              </span>
            </span>
            <span className="text-xs text-muted-foreground">
              {creator.email}
            </span>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-col">
            <span
              className={
                creator.status === "ACTIVE"
                  ? "font-medium text-emerald-500"
                  : "font-medium text-muted-foreground"
              }
            >
              {creator.status === "ACTIVE"
                ? t("creatorStatusActive")
                : t("creatorStatusInactive")}
            </span>
            <span className="text-xs text-muted-foreground">
              {creator.statusChangedAt
                ? `(${formatDateShort(creator.statusChangedAt)})`
                : "—"}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-right font-mono">
          {creator.receivableCount}
        </TableCell>
        <TableCell className="text-right">
          {creator.lastPaymentDate ? (
            formatDateShort(creator.lastPaymentDate)
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-right font-mono">
          {formatCurrency(currency, creator.totalShareAmount)}
        </TableCell>
        <TableCell className="text-right font-mono">
          {formatPercent(creator.avgSharePercentage, i18n.language)}
        </TableCell>
        <TableCell className="text-right font-mono">
          {formatCurrency(currency, creator.totalPaid)}
        </TableCell>
        <TableCell className="text-right font-mono">
          {formatCurrency(currency, creator.totalPending)}
        </TableCell>
      </TableRow>
      {isExpanded && canExpand && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={9} className="p-0 bg-muted/10">
            <CreatorReceivablesByMonth
              receivables={creator.receivables}
              currency={currency}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
